import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, ChevronRight, FileText, RefreshCw, Upload } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage, apiErrorType } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";
import { HeaderRow, TH, TableShell, rowLink } from "@/components/PageUi";
import { versionLabel } from "@/lib/expense-approval-policy-utils";
import { cn } from "@/lib/utils";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export type ExpensePolicyDocumentResponse = components["schemas"]["ExpensePolicyDocumentResponse"];
export type ExpensePolicyDocumentStatus = components["schemas"]["ExpensePolicyDocumentStatus"];

/** Compiling a document takes 10 to 30 seconds; this is how often a Compiling one is checked on. */
export const POLICY_DOCUMENT_POLL_MS = 3000;

const ACCEPT =
  ".pdf,.txt,.docx,application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const STATUS_META: Record<ExpensePolicyDocumentStatus, { label: string; className?: string }> = {
  Compiling: { label: "Reading" },
  ReadyForReview: { label: "Ready for review", className: "border-amber-500/30 bg-amber-500/15 text-amber-600" },
  Confirmed: { label: "In force", className: "border-emerald-500/30 bg-emerald-500/15 text-emerald-600" },
  Failed: { label: "Failed", className: "border-destructive/30 bg-destructive/15 text-destructive" },
  Superseded: { label: "Superseded" },
};

export function PolicyDocumentStatusBadge({ status }: { status?: ExpensePolicyDocumentStatus | null }) {
  const meta = status ? STATUS_META[status] : undefined;
  if (!meta) return <Badge variant="secondary">{status ?? "-"}</Badge>;
  if (!meta.className) {
    return (
      <Badge variant="secondary">
        {status === "Compiling" ? <Spinner className="size-3" /> : null}
        {meta.label}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className={meta.className}>
      {meta.label}
    </Badge>
  );
}

// The two refusals a person can fix themselves get their fix spelled out; the rest use the API's detail.
function uploadErrorMessage(error: unknown, fileName: string): string {
  const type = apiErrorType(error);
  if (type.includes("ExpensePolicyDocument.LegacyWordFormat")) {
    return `${fileName} is an old Word (.doc) file, which can't be read. Save it as .docx or PDF and upload that.`;
  }
  if (type.includes("ExpensePolicyDocument.NoText")) {
    return `${fileName} has no readable text, so it is probably a scan. Upload a PDF with selectable text, a .docx or a .txt file.`;
  }
  return apiErrorMessage(error, `Couldn't upload ${fileName}.`);
}

export interface ExpensePolicyDocumentsPanelProps {
  onOpenDocument?: (documentId: string) => void;
}

/** Each upload is a new version, read into proposed policies in the background; nothing applies until an admin confirms. */
export function ExpensePolicyDocumentsPanel({ onOpenDocument }: ExpensePolicyDocumentsPanelProps) {
  const api = useDolfinClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [documents, setDocuments] = useState<ExpensePolicyDocumentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  // A quiet fetch is the background poll: it keeps the list on screen and the last good copy if a tick fails.
  const fetchDocuments = useCallback(
    async ({ quiet = false }: { quiet?: boolean } = {}) => {
      if (!quiet) {
        setLoading(true);
        setError(null);
      }
      const { data, error: apiError } = await api.GET("/v1/expenses/policy-documents", {});
      if (apiError || !data) {
        if (!quiet) setError(apiErrorMessage(apiError, "Couldn't load your policy documents."));
      } else {
        setDocuments(data);
      }
      if (!quiet) setLoading(false);
    },
    [api],
  );

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const compiling = documents.some((d) => d.status === "Compiling");

  useEffect(() => {
    if (!compiling) return;
    const id = setInterval(() => void fetchDocuments({ quiet: true }), POLICY_DOCUMENT_POLL_MS);
    return () => clearInterval(id);
  }, [compiling, fetchDocuments]);

  async function handleUpload(files: File[]) {
    const file = files[0];
    if (!file) return;
    if (files.length > 1) toast.error("Upload one policy document at a time.");

    setUploading(true);
    const body = new FormData();
    body.append("file", file);
    // openapi-fetch would serialise a JSON body; multipart has to go through as-is.
    const { error: apiError } = await api.POST("/v1/expenses/policy-documents", {
      body: body as unknown as never,
      bodySerializer: (b: unknown) => b as BodyInit,
    });
    setUploading(false);
    if (fileInput.current) fileInput.current.value = "";
    if (apiError) {
      toast.error(uploadErrorMessage(apiError, file.name));
      return;
    }
    toast.success("Policy uploaded. Reading it now, which takes up to half a minute.");
    await fetchDocuments({ quiet: true });
  }

  async function retry(doc: ExpensePolicyDocumentResponse) {
    setRetryingId(doc.id);
    const { error: apiError } = await api.POST("/v1/expenses/policy-documents/{id}/retry", {
      params: { path: { id: doc.id } },
    });
    setRetryingId(null);
    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Couldn't retry reading the document."));
      return;
    }
    toast.success("Reading the document again.");
    await fetchDocuments({ quiet: true });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Policy document</CardTitle>
        <CardDescription>
          Upload your written expense policy and Dolfin proposes approval policies from it, each citing the
          clause it enforces. Nothing applies until an admin reviews and confirms it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <input
          ref={fileInput}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => handleUpload(Array.from(e.target.files ?? []))}
        />
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload a policy document"
          className={cn(
            "rounded-xl border-2 border-dashed px-6 py-5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            dragOver
              ? "border-accent-blue/60 bg-accent-blue/5"
              : "border-muted-foreground/25 hover:border-muted-foreground/50",
            uploading ? "pointer-events-none opacity-70" : "cursor-pointer",
          )}
          onClick={() => !uploading && fileInput.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !uploading) fileInput.current?.click();
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (!uploading) handleUpload(Array.from(e.dataTransfer.files));
          }}
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Spinner className="size-4" /> Uploading policy...
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm">
              <Upload className="size-5 text-muted-foreground" />
              <span className="font-medium">Drop your expense policy here, or click to browse</span>
              <span className="text-xs text-muted-foreground">PDF · Word (.docx) · Text</span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner className="size-4" /> Loading policy documents...
          </div>
        ) : error ? (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
            <span className="text-destructive">{error}</span>
            <Button variant="outline" size="sm" onClick={() => fetchDocuments()}>
              Try again
            </Button>
          </div>
        ) : documents.length === 0 ? null : (
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={`w-[64px] ${TH}`}>Version</TableHead>
                  <TableHead className={TH}>File</TableHead>
                  <TableHead className={TH}>Status</TableHead>
                  <TableHead className={TH}>Found</TableHead>
                  <TableHead className={TH}>Uploaded</TableHead>
                  <TableHead className="w-[120px]" />
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => {
                  const open = onOpenDocument ? () => onOpenDocument(doc.id) : undefined;
                  const proposed = Number(doc.proposedPolicyCount ?? 0);
                  const unenforceable = Number(doc.unenforceableClauseCount ?? 0);
                  return (
                    <TableRow key={doc.id} {...rowLink(open)}>
                      <TableCell className="font-mono text-xs text-muted-foreground tabular-nums">
                        {versionLabel(doc.version)}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex min-w-0 items-center gap-1.5">
                          <FileText className="size-3.5 shrink-0 text-muted-foreground" />
                          <span className="truncate">{doc.fileName}</span>
                        </span>
                        {doc.status === "Failed" && doc.failureReason ? (
                          <span className="mt-1 flex items-start gap-1 text-xs text-destructive">
                            <AlertTriangle className="mt-0.5 size-3 shrink-0" />
                            {doc.failureReason}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <PolicyDocumentStatusBadge status={doc.status} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {doc.status === "Compiling" || doc.status === "Failed"
                          ? "-"
                          : `${proposed} ${proposed === 1 ? "policy" : "policies"}${
                              unenforceable > 0 ? ` · ${unenforceable} not enforced` : ""
                            }`}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDateTime(doc.createdAt)}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        {doc.status === "Failed" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={retryingId === doc.id}
                            onClick={() => retry(doc)}
                          >
                            {retryingId === doc.id ? <Spinner className="size-3.5" /> : <RefreshCw className="size-3.5" />}
                            Retry
                          </Button>
                        ) : doc.status === "ReadyForReview" && open ? (
                          <Button size="sm" onClick={open}>
                            Review
                          </Button>
                        ) : open && doc.status !== "Compiling" ? (
                          <Button variant="ghost" size="sm" onClick={open}>
                            View <ChevronRight className="size-3.5" />
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableShell>
        )}
      </CardContent>
    </Card>
  );
}
